import type { ShopBean, BeanMethod } from '../../services/shop-service';

/**
 * "What we're pouring" — the beans a shop serves, shown to customers on the
 * shop's surface. Deliberately plain: roaster + bean name + method only, no
 * logo/link/media. That plainness is the business-model guardrail — paid
 * roaster features (Bean of the Week) keep the reach, media and link that make
 * them worth $. Renders nothing when a shop hasn't listed any beans.
 */

const METHOD_LABEL: Record<BeanMethod, string> = {
  espresso: 'ESPRESSO', filter: 'FILTER', decaf: 'DECAF', other: 'ALSO POURING',
};
// Stable display order regardless of how the owner entered them.
const METHOD_ORDER: BeanMethod[] = ['espresso', 'filter', 'decaf', 'other'];

/** How many beans HOME's narrow column shows before collapsing to "+N more". */
const COMPACT_LIMIT = 2;

export default function ShopBeans({ beans, compact }: { beans?: ShopBean[] | null; compact?: boolean }) {
  if (!beans || beans.length === 0) return null;

  const ordered = [...beans].sort(
    (a, b) => METHOD_ORDER.indexOf(a.method) - METHOD_ORDER.indexOf(b.method),
  );
  // HOME's split row gives this card ~167px. Four beans at ~44px each would
  // blow the ~35px of headroom the whole screen has, so the narrow variant
  // caps the list and points at the shop modal for the rest.
  const shown = compact ? ordered.slice(0, COMPACT_LIMIT) : ordered;
  const hidden = ordered.length - shown.length;

  return (
    // 2px ink trim via an ink-filled wrapper — `pixel-corners` is a clip-path,
    // so a CSS border would be sliced off at every step.
    <div className="w-full pixel-corners bg-primary p-[2px]">
    <div className={`w-full h-full bg-white pixel-corners ${compact ? 'p-[12px]' : 'p-[16px]'}`}>
      <p className="font-heading text-[8px] text-body-grey tracking-[1px] mb-2 text-center">WHAT WE'RE POURING</p>
      <ul className="space-y-1.5">
        {shown.map((bean, i) => (
          <li key={i}>
            {compact ? (
              // Narrow column: the pill's background and padding cost ~35px
              // per bean that the column cannot afford, so the method drops to
              // a micro-label above the name. Measured at 390px: this is what
              // takes the row from 212px to 142px.
              <>
                <p className="font-heading text-[7px] text-body-grey leading-none mb-0.5">
                  {METHOD_LABEL[bean.method]}
                </p>
                <p className="font-grotesk font-medium text-[11px] text-body-grey leading-tight">
                  <span className="font-bold text-primary">{bean.name}</span> · {bean.roaster}
                </p>
              </>
            ) : (
              <span className="flex items-start gap-2">
                <span className="font-heading text-[8px] text-body-grey bg-tab-inactive pixel-corners-sm px-2 py-1 shrink-0 mt-0.5">
                  {METHOD_LABEL[bean.method]}
                </span>
                <span className="font-grotesk font-medium text-xs text-body-grey leading-tight">
                  <span className="font-bold text-primary">{bean.name}</span>
                  <span> · {bean.roaster}</span>
                </span>
              </span>
            )}
          </li>
        ))}
      </ul>
      {hidden > 0 && (
        <p className="font-heading text-[7px] text-body-grey mt-1.5">+{hidden} MORE</p>
      )}
    </div>
    </div>
  );
}
